import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import agent from "../../agent";
import { USERS_LOAD, USERS_UNLOAD } from "../../constants/actionTypes";

const ALL_ROLES = ["ADMIN", "CONSULTANT", "USER", "OWNER", "SUPER", "API", "LOGIST", "ACCOUNTANT"];

function Users() {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const usersPage = useSelector((state) => state.users);
  const users = usersPage?.users;

  const [rows, setRows] = useState(20);
  const [first, setFirst] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", firstName: "", middleName: "", lastName: "", phone: "", address: "", roles: ALL_ROLES.reduce((acc, r) => (acc[r] = false, acc), {}) });
  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ id: null, email: "", firstName: "", middleName: "", lastName: "", phone: "", address: "", statusType: "", roles: ALL_ROLES.reduce((acc, r) => (acc[r] = false, acc), {}) });
  const [editLoading, setEditLoading] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [filterEmail, setFilterEmail] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const loadUsers = async (pageIndex, pageSize) => {
    setLoading(true);
    try {
      const filters = {};
      if (filterEmail && filterEmail.trim()) filters.email = filterEmail.trim();
      if (filterStatus) filters.statusType = filterStatus;
      const data = await agent.Users.getUsers(pageIndex, pageSize, filters);
      dispatch({ type: USERS_LOAD, payload: data });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить пользователей", life: 4000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const pageIndex = Math.floor(first / rows);
    loadUsers(pageIndex, rows);
    return () => dispatch({ type: USERS_UNLOAD });
  }, [dispatch, first, rows, filterEmail, filterStatus]);

  const onPage = (e) => {
    setRows(e.rows);
    setFirst(e.first);
  };

  const handleLock = async (row) => {
    try {
      await agent.Users.lock(row.id);
      toast.current?.show({ severity: "success", summary: "Успех", detail: `Пользователь ${row.email} заблокирован`, life: 3000 });
      const pageIndex = Math.floor(first / rows);
      await loadUsers(pageIndex, rows);
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Ошибка", detail: "Не удалось заблокировать пользователя", life: 4000 });
    }
  };

  const handleUnlock = async (row) => {
    try {
      await agent.Users.unlock(row.id);
      toast.current?.show({ severity: "success", summary: "Успех", detail: `Пользователь ${row.email} разблокирован`, life: 3000 });
      const pageIndex = Math.floor(first / rows);
      await loadUsers(pageIndex, rows);
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Ошибка", detail: "Не удалось разблокировать пользователя", life: 4000 });
    }
  };

  const handleResetPassword = async (row) => {
    try {
      await agent.Users.resetPassword(row.id);
      toast.current?.show({ severity: "success", summary: "Успех", detail: `Пароль пользователя ${row.email} сброшен`, life: 3000 });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Ошибка", detail: "Не удалось сбросить пароль", life: 4000 });
    }
  };

  const statusTagTemplate = (row) => {
    const value = row?.statusType || "";
    const severity = value === "ACTIVE" ? "success" : ["BLOCKED", "PWD_BLOCKED"].includes(value) ? "danger" : "info";
    let label = ""
      switch (value) {
          case "ACTIVE":
              label = 'Активный'
              break;
          case "BLOCKED":
              label = 'Заблокированный'
              break;
          case 'PWD_BLOCKED':
              label = 'Заблокированный неверный пароль'
              break
          default:
            label = value
      }

    return <Tag value={label} severity={severity} />;
  };

  const actionsTemplate = (row) => {
    const isLocked = ["LOCKED", "BLOCKED", "PWD_BLOCKED"].includes(row?.statusType);
    return (
      <div className="p-d-flex p-ai-center" style={{ gap: 6 }}>
        <Button icon="pi pi-eye" className="p-button-rounded p-button-text p-button-secondary" onClick={() => openViewDialog(row.id)} tooltip="Просмотр" tooltipOptions={{ position: 'top' }} />
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" onClick={() => openEditDialog(row.id)} tooltip="Редактировать" tooltipOptions={{ position: 'top' }} />
        {isLocked ? (
          <Button icon="pi pi-lock-open" className="p-button-rounded p-button-text p-button-success" onClick={() => handleUnlock(row)} tooltip="Разблокировать" tooltipOptions={{ position: 'top' }} />
        ) : (
          <Button icon="pi pi-lock" className="p-button-rounded p-button-text p-button-warning" onClick={() => handleLock(row)} tooltip="Заблокировать" tooltipOptions={{ position: 'top' }} />
        )}
        <Button icon="pi pi-refresh" className="p-button-rounded p-button-text p-button-help" onClick={() => handleResetPassword(row)} tooltip="Сброс пароля" tooltipOptions={{ position: 'top' }} />
      </div>
    );
  };

  const openEditDialog = async (id) => {
    setIsViewMode(false);
    setShowEdit(true);
    setEditId(id);
    setEditForm({ id: null, email: "", firstName: "", middleName: "", lastName: "", phone: "", address: "", statusType: "", roles: ALL_ROLES.reduce((acc, r) => (acc[r] = false, acc), {}) });
    setEditLoading(true);
    try {
      const data = await agent.Users.getUser(id);
      const rolesMap = (Array.isArray(data?.roles) ? data.roles : []).reduce((acc, r) => {
        acc[r.role] = !!r.assigned;
        return acc;
      }, ALL_ROLES.reduce((acc, r) => (acc[r] = false, acc), {}));
      setEditForm({
        id: data?.id ?? id,
        email: data?.email ?? "",
        firstName: data?.firstName ?? "",
        middleName: data?.middleName ?? "",
        lastName: data?.lastName ?? "",
        phone: data?.phone ?? "",
        address: data?.address ?? "",
        statusType: data?.statusType ?? "",
        roles: rolesMap,
      });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить пользователя", life: 4000 });
      setShowEdit(false);
    } finally {
      setEditLoading(false);
    }
  };

  const openViewDialog = async (id) => {
    setIsViewMode(true);
    setShowEdit(true);
    setEditId(id);
    setEditForm({ id: null, email: "", firstName: "", middleName: "", lastName: "", phone: "", address: "", statusType: "", roles: ALL_ROLES.reduce((acc, r) => (acc[r] = false, acc), {}) });
    setEditLoading(true);
    try {
      const data = await agent.Users.getUser(id);
      const rolesMap = (Array.isArray(data?.roles) ? data.roles : []).reduce((acc, r) => {
        acc[r.role] = !!r.assigned;
        return acc;
      }, ALL_ROLES.reduce((acc, r) => (acc[r] = false, acc), {}));
      setEditForm({ id: data?.id ?? id, email: data?.email ?? "", firstName: data?.firstName ?? "", middleName: data?.middleName ?? "", lastName: data?.lastName ?? "", phone: data?.phone ?? "", address: data?.address ?? "", statusType: data?.statusType ?? "", roles: rolesMap });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить пользователя", life: 4000 });
      setShowEdit(false);
    } finally {
      setEditLoading(false);
    }
  };

  const saveEdit = async () => {
    if (!editForm.id) return;
    try {
      const payload = { 
        email: editForm.email,
        firstName: editForm.firstName,
        middleName: editForm.middleName,
        lastName: editForm.lastName,
        phone: editForm.phone,
        address: editForm.address,
        roles: ALL_ROLES.filter((r) => !!editForm.roles[r])
      };
      await agent.Users.updateUser(editForm.id, payload);
      toast.current?.show({ severity: "success", summary: "Сохранено", detail: "Пользователь обновлен", life: 3000 });
      setShowEdit(false);
      const pageIndex = Math.floor(first / rows);
      await loadUsers(pageIndex, rows);
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Ошибка", detail: agent.handleError(e), life: 4000 });
    }
  };

  const createUser = async () => {
    try {
      const payload = { 
        email: createForm.email,
        firstName: createForm.firstName,
        middleName: createForm.middleName,
        lastName: createForm.lastName,
        phone: createForm.phone,
        address: createForm.address,
        roles: ALL_ROLES.filter((r) => !!createForm.roles[r])
      };
      await agent.Users.createUser(payload);
      toast.current?.show({ severity: "success", summary: "Создано", detail: "Пользователь создан", life: 3000 });
      setShowCreate(false);
      setCreateForm({ email: "", firstName: "", middleName: "", lastName: "", phone: "", address: "", roles: ALL_ROLES.reduce((acc, r) => (acc[r] = false, acc), {}) });
      const pageIndex = Math.floor(first / rows);
      await loadUsers(pageIndex, rows);
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Ошибка", detail: agent.handleError(e), life: 4000 });
    }
  };

  const toolbar = (
    <div className="p-d-flex p-ai-center p-jc-between" style={{ marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
      <div className="p-d-flex p-ai-center" style={{ gap: 8 }}>
        <Button label="Создать пользователя" icon="pi pi-user-plus" onClick={() => setShowCreate(true)} />
      </div>
      <div className="p-d-flex p-ai-center" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input
          type="text"
          className="p-inputtext p-component"
          placeholder="Email"
          value={filterEmail}
          onChange={(e) => { setFilterEmail(e.target.value); setFirst(0); }}
          style={{ minWidth: 220 }}
        />
        <select
          className="p-inputtext p-component"
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setFirst(0); }}
          style={{ minWidth: 220 }}
        >
          <option value="">Все статусы</option>
          <option value="ACTIVE">Активный</option>
          <option value="BLOCKED">Заблокированный</option>
          <option value="PWD_BLOCKED">Заблокированный неверный пароль</option>
        </select>
      </div>
    </div>
  );

  const editDialog = (
    <Dialog header={`Пользователь #${editForm.id || ''}`} visible={showEdit} style={{ width: '32rem' }} modal onHide={() => setShowEdit(false)}
            footer={
              <div>
                <Button label="Отмена" className="p-button-text p-mr-2" onClick={() => setShowEdit(false)} />
                {!isViewMode && <Button label="Сохранить" icon="pi pi-save" onClick={saveEdit} />}
              </div>
            }>
      {editLoading ? (
        <div>Загрузка...</div>
      ) : (
        <div className="p-fluid" style={{ display: 'grid', gap: 12 }}>
          <div>
            <label className="p-d-block p-mb-1">Email</label>
            <input className="p-inputtext p-component" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} disabled={isViewMode} />
          </div>
          <div>
            <label className="p-d-block p-mb-1">Имя</label>
            <input className="p-inputtext p-component" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} disabled={isViewMode} />
          </div>
          <div>
            <label className="p-d-block p-mb-1">Отчество</label>
            <input className="p-inputtext p-component" value={editForm.middleName} onChange={(e) => setEditForm({ ...editForm, middleName: e.target.value })} disabled={isViewMode} />
          </div>
          <div>
            <label className="п-d-block p-mb-1">Фамилия</label>
            <input className="p-inputtext p-component" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} disabled={isViewMode} />
          </div>
          <div>
            <label className="p-d-block p-mb-1">Телефон</label>
            <input className="p-inputtext p-component" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} disabled={isViewMode} />
          </div>
          <div>
            <label className="p-d-block p-mb-1">Адрес</label>
            <input className="p-inputtext p-component" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} disabled={isViewMode} />
          </div>
          <div>
            <label className="p-d-block p-mb-1">Статус</label>
            <input className="p-inputtext p-component" value={editForm.statusType} disabled />
          </div>
          <div>
            <label className="p-d-block p-mb-2">Роли</label>
            <div className="p-d-flex p-ai-center" style={{ flexWrap: 'wrap', gap: 12 }}>
              {ALL_ROLES.map((r) => (
                <label key={r} className="p-d-flex p-ai-center" style={{ gap: 6 }}>
                  <input type="checkbox" disabled={isViewMode} checked={!!editForm.roles[r]} onChange={(e) => setEditForm({ ...editForm, roles: { ...editForm.roles, [r]: e.target.checked } })} />
                  <span>{r}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );

  const createDialog = (
    <Dialog header="Создать пользователя" visible={showCreate} style={{ width: '32rem' }} modal onHide={() => setShowCreate(false)}
            footer={
              <div>
                <Button label="Отмена" className="p-button-text p-mr-2" onClick={() => setShowCreate(false)} />
                <Button label="Создать" icon="pi pi-check" onClick={createUser} />
              </div>
            }>
      <div className="p-fluid" style={{ display: 'grid', gap: 12 }}>
        <div>
          <label className="p-d-block p-mb-1">Email</label>
          <input className="p-inputtext p-component" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
        </div>
        <div>
          <label className="p-d-block p-mb-1">Имя</label>
          <input className="p-inputtext p-component" value={createForm.firstName} onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })} />
        </div>
        <div>
          <label className="p-d-block p-mb-1">Отчество</label>
          <input className="p-inputtext p-component" value={createForm.middleName} onChange={(e) => setCreateForm({ ...createForm, middleName: e.target.value })} />
        </div>
        <div>
          <label className="p-d-block p-mb-1">Фамилия</label>
          <input className="p-inputtext p-component" value={createForm.lastName} onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })} />
        </div>
        <div>
          <label className="p-d-block p-mb-1">Телефон</label>
          <input className="p-inputtext p-component" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} />
        </div>
        <div>
          <label className="p-d-block p-mb-1">Адрес</label>
          <input className="p-inputtext p-component" value={createForm.address} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} />
        </div>
        <div>
          <label className="p-d-block p-mb-2">Роли</label>
          <div className="p-d-flex p-ai-center" style={{ flexWrap: 'wrap', gap: 12 }}>
            {ALL_ROLES.map((r) => (
              <label key={r} className="p-d-flex p-ai-center" style={{ gap: 6 }}>
                <input type="checkbox" checked={!!createForm.roles[r]} onChange={(e) => setCreateForm({ ...createForm, roles: { ...createForm.roles, [r]: e.target.checked } })} />
                <span>{r}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );

  return (
    <div className="datatable-doc-demo">
      <Toast ref={toast} />
      <div className="card">
        <h2>Пользователи</h2>
        {toolbar}
        <DataTable
          value={users?.content || []}
          lazy
          totalRecords={users?.totalElements || 0}
          paginator
          rows={rows}
          first={(users?.number || 0) * (users?.size || rows)}
          onPage={onPage}
          rowsPerPageOptions={[10, 20, 50]}
          loading={loading}
          dataKey="id"
          rowHover
          emptyMessage="Данные не найдены"
          currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        >
          <Column headerStyle={{ width: '80px' }} columnKey="id" field="id" header="ID" sortable />
          <Column headerStyle={{ width: '260px' }} columnKey="email" field="email" header="Email" sortable />
          <Column headerStyle={{ width: '160px' }} columnKey="firstName" field="firstName" header="Имя" sortable />
          <Column headerStyle={{ width: '160px' }} columnKey="lastName" field="lastName" header="Фамилия" sortable />
          <Column headerStyle={{ width: '160px' }} header="Статус" body={statusTagTemplate} />
          <Column headerStyle={{ width: '320px' }} header="Действия" body={actionsTemplate} />
        </DataTable>
        {editDialog}
        {createDialog}
      </div>
    </div>
  );
}

export default Users;